import { postWithAuth } from "@/lib/api-helpers";
import { extractNoteId } from "@/utils/noteUrl";
import { createImageUpload } from "novel";
import { toast } from "@/lib/toast";
import { MAX_EDITOR_IMAGE_SIZE_MB, MAX_UPLOAD_SIZE_MB, fileTooLargeMessage } from "@/lib/upload-limits";
import { compressImageFile } from "@/lib/compressImage";

const onUpload = async (rawFile: File) => {
  const file = await compressImageFile(rawFile);
  const filePath = `docs/image/${file?.name || "image.png"}`;
  const arrayBuffer = await file.arrayBuffer();
  // Non-empty fallback: the route needs a note id segment (POST /api/note/upload/:noteId).
  const noteId = extractNoteId(window.location.pathname.split("/").filter(Boolean).pop()) || "misc";

  const promise = postWithAuth(`/api/note/upload/${noteId}`, arrayBuffer, {
    headers: {
      "content-type": file?.type || "application/octet-stream",
      "x-vercel-filename": encodeURIComponent(filePath),
    },
    body: arrayBuffer,
  });

  return new Promise((resolve, reject) => {
    toast.promise(
      promise.then(async (res) => {
        // Successfully uploaded image
        if (res.status === 200 || res.status === 201) {
          const { url } = res as { url: string };
          // preload the image
          const image = new Image();
          image.onload = () => {
            resolve(url);
          };
          image.onerror = (e) => {
            console.error("Failed to load image", e);
            // resolve(url); // Optionally remove this to catch broken image load
            reject(new Error("Uploaded, but image failed to load. Possible CORS or format issue."));
          };
          image.src = url;
          // No blob store configured
        } else if (res.status === 401) {
          resolve(file);
          throw new Error("`BLOB_READ_WRITE_TOKEN` environment variable not found, reading image locally instead.");
          // Unknown error
        } else {
          throw new Error(res?.message || "Error uploading image. Please try again.");
        }
      }),
      {
        loading: "Uploading image...",
        success: "Image uploaded successfully.",
        error: (e) => {
          reject(e);
          return e.message;
        },
      },
    );
  });
};

export const uploadFn = createImageUpload({
  onUpload,
  validateFn: (file) => {
    if (!file.type.includes("image/")) {
      toast.error("File type not supported.");
      return false;
    }
    if (file.size / 1024 / 1024 > MAX_EDITOR_IMAGE_SIZE_MB) {
      toast.error(fileTooLargeMessage(MAX_EDITOR_IMAGE_SIZE_MB));
      return false;
    }
    return true;
  },
});

// Returns the uploaded file URL on success
export async function uploadCoverImage(
  file: File,
  params?: { noteId?: string; parentId?: string },
): Promise<string> {
  // Reuse validation rules
  if (!file.type.includes("image/")) {
    toast.error("File type not supported.");
    throw new Error("Unsupported file type");
  }
  if (file.size / 1024 / 1024 > MAX_UPLOAD_SIZE_MB) {
    toast.error(fileTooLargeMessage(MAX_UPLOAD_SIZE_MB));
    throw new Error("File too large");
  }

  // The backend route is POST /api/note/upload/:noteId and 404s without a
  // non-empty id segment. Callers without a note context (e.g. profile photo
  // or cover image in Settings) must pass a stable namespacing id such as the
  // user id. Fail loudly here instead of firing a request that silently 404s.
  const noteId = params?.noteId?.trim();
  if (!noteId) {
    toast.error("Couldn't upload image. Please try again.");
    throw new Error("uploadCoverImage: a non-empty noteId is required");
  }

  const compressed = await compressImageFile(file);
  const fileName = compressed?.name || "cover.png";
  const filePath = `docs/covers/${noteId}/${fileName}`;
  const arrayBuffer = await compressed.arrayBuffer();

  const promise = postWithAuth(`/api/note/upload/${params?.noteId || "covers"}`, arrayBuffer, {
    headers: {
      "content-type": compressed?.type || "application/octet-stream",
      "x-vercel-filename": encodeURIComponent(filePath),
    },
    body: arrayBuffer,
  }).then(async (res) => {
    if (res.status === 200 || res.status === 201) {
      const { url } = res as { url: string };
      return url;
    }
    if (res.status === 401) {
      // fall back to local read if blob store is not configured
      return URL.createObjectURL(new Blob([arrayBuffer], { type: compressed.type }));
    }
    throw new Error(res?.message || "Error uploading image. Please try again.");
  });

  return new Promise<string>((resolve, reject) => {
    toast.promise(
      promise.then((url) => {
        const image = new Image();
        image.onload = () => resolve(url);
        image.onerror = (e) => {
          console.error("Failed to load image", e);
          reject(new Error("Uploaded, but image failed to load. Possible CORS or format issue."));
        };
        image.src = url;
      }),
      {
        loading: "Uploading image...",
        success: "Image uploaded successfully.",
        error: (e) => {
          reject(e);
          return (e as Error).message;
        },
      },
    );
  });
}
